import { useState, useRef } from 'react';
import ContentHeader from '../components/ContentHeader';
import SelectTickets from './SelectTickets';
import Steps from '../components/Steps';
import Footer from '../components/Footer';
import MobileBottomBar from '../components/MobileBottomBar';
import { useCart } from '../hooks/useCart';
import NextToEvent from '../components/NextToEvent';
import { ShowSuccessResponseResponse } from '../../openapi/model';
import { useTranslation } from 'react-i18next';
import { useMarketing } from '../hooks/useMarketing.tsx';
import { getTimeslots, useGetTimeslots } from '../../openapi/timeslots/timeslots';
import { getNormalizedShowId } from '../helpers/show';
import TimeslotCancelledBanner from '../components/TimeslotCancelledBanner';
import { buildGa4ItemsFromCart, sumGa4ItemsValue } from '../helpers/ga4Items';

const CURRENT_STEP = 2;

function Step2({
    setStep,
    show,
    stepNeedsStep3 = false,
    delayedSeatAssignmentEnabled = false,
    onOpenWaitlist,
}: {
    setStep: (step: number) => void;
    show: ShowSuccessResponseResponse;
    stepNeedsStep3?: boolean;
    delayedSeatAssignmentEnabled?: boolean;
    onOpenWaitlist: () => void;
}) {
    const { cart, timeslot, isPricingLoading } = useCart();
    const { t } = useTranslation();
    const { addToCart, beginCheckout } = useMarketing();
    const normalizedShowId = getNormalizedShowId(show);
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    const isSubmittingRef = useRef(false);

    // A cancelled timeslot disables booking: show the reason in place of the
    // ticket picker and block advancing. The backend also refuses the booking.
    const { data: { timeslots: [selectedTimeslotData] = [] } = { timeslots: [] } } =
        useGetTimeslots(
            { idList: timeslot?.id ? [timeslot.id] : [] },
            { query: { enabled: Boolean(timeslot?.id) } },
        );
    const cancellationReason = selectedTimeslotData?.cancellationReason;
    const isCancelled = Boolean(cancellationReason && cancellationReason.trim());

    // if step3 is needed, the next step label changes
    const nextStepLabel = stepNeedsStep3
        ? t('widgetStepNavigation.step2next')
        : t('widgetStepNavigation.step3next');
    // only tickets exists in cart which have 'can buy alone' is false
    const canBuyAloneError =
        cart.length > 0 && cart.every((ticket) => ticket.canBuyAlone === false);

    const hasChildOrInfant = cart.some(
        (ticket) => ticket.ageBand === 'child' || ticket.ageBand === 'infant',
    );
    const adultTicketRequiredError =
        !!show.marketing?.requireAdultTicket &&
        hasChildOrInfant &&
        !cart.some((ticket) => ticket.ageBand === 'adult');

    const handleNextStep = async () => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        if (timeslot?.id && normalizedShowId) {
            setIsCheckingAvailability(true);
            try {
                const { timeslots: [timeslotData] = [] } = await getTimeslots({
                    idList: [timeslot.id],
                    includeBlocks: true,
                });

                if (timeslotData?.ticketTypeBlocks?.length) {
                    const blocks = timeslotData.ticketTypeBlocks;
                    const requestedByType = cart.reduce(
                        (acc, item) => {
                            acc[item.ticketTypeId] = (acc[item.ticketTypeId] || 0) + item.quantity;
                            return acc;
                        },
                        {} as Record<number, number>,
                    );

                    const hasAvailabilityIssue = Object.entries(requestedByType).some(
                        ([ticketTypeIdRaw, requestedQuantity]) => {
                            const block = blocks.find(
                                (b) => b.ticketTypeId === Number(ticketTypeIdRaw),
                            );
                            return block ? requestedQuantity > block.available : false;
                        },
                    );

                    if (hasAvailabilityIssue) {
                        onOpenWaitlist();
                        return;
                    }
                }
            } catch (availabilityCheckError) {
                console.error(
                    'Availability pre-check failed, continuing booking flow:',
                    availabilityCheckError,
                );
            } finally {
                setIsCheckingAvailability(false);
            }
        }

        // Track add to cart when advancing to next step
        if (cart.length > 0) {
            const cartItems = buildGa4ItemsFromCart(cart, show.title);
            const value = sumGa4ItemsValue(cartItems);

            addToCart({ items: cartItems, value, currency: 'USD' });

            // begin_checkout fires here when step 3 is skipped (going directly to checkout);
            // when step 3 is needed it fires in Step3's navigation handler instead.
            if (!stepNeedsStep3) {
                beginCheckout({ items: cartItems, value, currency: 'USD' });
            }
        }
        setStep(CURRENT_STEP + 1);
    };

    return (
        <div className="fireknife-step fireknife-step2">
            <div className="fireknife-step__main fireknife-step2__main">
                <div className="fireknife-step2__content-header">
                    <ContentHeader
                        prevStep={() => {
                            setStep(CURRENT_STEP - 1);
                        }}
                        label={t('widgetStepNavigation.step2back')}
                    />
                </div>
                <div className="fireknife-step2__select-tickets">
                    {cancellationReason && cancellationReason.trim() ? (
                        <TimeslotCancelledBanner reason={cancellationReason} />
                    ) : (
                        <SelectTickets show={show} />
                    )}
                </div>
                <Footer />
            </div>
            <aside className="fireknife-step__aside fireknife-step2__aside">
                <MobileBottomBar
                    showFees={show.fees}
                    isBreakingOutFees={show.isBreakingOutFees}
                    priceListingMode={show.priceListingMode}
                    cardProcessingFee={show.cardProcessingFee}
                    nextStep={handleNextStep}
                    label={nextStepLabel}
                    disabled={
                        cart.length === 0 ||
                        canBuyAloneError ||
                        adultTicketRequiredError ||
                        isCheckingAvailability ||
                        isCancelled ||
                        isPricingLoading
                    }
                />
                <div className="fireknife-step__aside-content">
                    <div className="fireknife-step2__payment">
                        <Steps
                            step={2}
                            setStep={setStep}
                            showId={normalizedShowId}
                            delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                            rentalMode={!!show.marketing?.rentalMode}
                        ></Steps>
                    </div>
                </div>
                <NextToEvent
                    nextStep={handleNextStep}
                    label={nextStepLabel}
                    disabled={
                        cart.length === 0 ||
                        canBuyAloneError ||
                        adultTicketRequiredError ||
                        isCheckingAvailability ||
                        isCancelled ||
                        isPricingLoading
                    }
                    showFees={show.fees}
                    isBreakingOutFees={show.isBreakingOutFees}
                    priceListingMode={show.priceListingMode}
                    cardProcessingFee={show.cardProcessingFee}
                />
            </aside>
        </div>
    );
}

export default Step2;
